import { defineStore } from 'pinia';
import { getMenusApi } from '@/api/user';
import defaultSettings from '@/setting';
import {
  crmebMenuToSidebarRoutes,
  crmebNodesToNavList,
  filterMenuByAccess,
  type CrmebMenuNode,
  type CrmebNavMenuItem,
} from '@/utils/crmebMenu';
import { normalizeRegionList } from '@/utils/crmebAdminRegion';
import {
  findFirstNonNullChildren,
  getHeaderName,
  getMenuSider,
} from '@/utils/crmebLayoutMenu';
import useCrmebThemeConfigStore from '@/store/modules/themeConfig';
import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import router from '@/router';

const MENU_STORAGE_KEY = 'MenuList';
const ZONE_ID_KEY = 'current_zone_id';

/** 区域代理 menus 请求参数（与 login/index.vue 一致） */
function buildMenuRequestParams(): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  try {
    const adminStr = localStorage.getItem('AdminInfo');
    if (adminStr) {
      const admin = JSON.parse(adminStr) as Record<string, unknown>;
      if (Number(admin?.isAgent) > 0) {
        const zid = localStorage.getItem(ZONE_ID_KEY);
        if (zid) {
          params.circleId = Number(zid);
        } else {
          const regions = normalizeRegionList(admin);
          if (regions[0]?.circleId) {
            params.circleId = regions[0].circleId;
          }
        }
      }
    }
  } catch {
    // ignore
  }
  return params;
}

const useCrmebMenuStore = defineStore('crmebMenu', {
  state: (): {
    menuTree: CrmebMenuNode[];
    sidebarRouters: RouteRecordRaw[];
    /** EB 分栏顶栏菜单 */
    columnsNavList: CrmebNavMenuItem[];
    /** EB 分栏左侧子菜单 */
    childNavList: CrmebNavMenuItem[];
    activeColumnIndex: number;
    access: string[];
    loaded: boolean;
  } => ({
    menuTree: [],
    sidebarRouters: [],
    columnsNavList: [],
    childNavList: [],
    activeColumnIndex: 0,
    access: [],
    loaded: false,
  }),
  actions: {
    setAccess(access: string[]) {
      this.access = access || [];
      if (this.menuTree.length) {
        this.rebuildSidebarFromTree();
      }
    },
    rebuildSidebarFromTree() {
      const filtered = this.access.length
        ? filterMenuByAccess(this.menuTree, this.access)
        : this.menuTree;
      this.sidebarRouters = crmebMenuToSidebarRoutes(
        filtered,
        defaultSettings.roterPre,
      ) as RouteRecordRaw[];
      this.columnsNavList = crmebNodesToNavList(filtered, defaultSettings.roterPre).map(
        (v, k) => ({ ...v, k }),
      );
      if (router.currentRoute.value?.path) {
        this.syncLayoutByRoute(router.currentRoute.value);
      }
    },
    setMenuTree(tree: CrmebMenuNode[]) {
      this.menuTree = tree || [];
      try {
        localStorage.setItem(MENU_STORAGE_KEY, JSON.stringify(this.menuTree));
      } catch {
        // ignore
      }
      this.rebuildSidebarFromTree();
      this.loaded = true;
    },
    /** 按当前路由同步分栏高亮与子菜单（columns / classic）；defaults 重置折叠 */
    syncLayoutByRoute(to: RouteLocationNormalized) {
      const theme = useCrmebThemeConfigStore();
      const layout = theme.themeConfig.layout;
      if (layout === 'defaults' || layout === 'transverse') {
        theme.setCollapse(false);
        return;
      }
      if (layout !== 'columns' && layout !== 'classic') {
        return;
      }
      const list = this.columnsNavList;
      if (!list.length) {
        this.childNavList = [];
        return;
      }
      const headerName = getHeaderName(to, list);
      const column =
        list.find((item) => item.path === headerName) ||
        list[0];
      const idx = column?.k ?? 0;
      this.activeColumnIndex = idx;
      const sider = getMenuSider(list, column?.path || list[0].path);
      const asideChildren = sider[0]?.children || [];
      this.childNavList = asideChildren;
      theme.setCollapse(asideChildren.length > 1 ? false : true);
    },
    onColumnClick(item: CrmebNavMenuItem) {
      const theme = useCrmebThemeConfigStore();
      if (item.children?.length) {
        const first = findFirstNonNullChildren(item.children);
        if (first?.path) {
          router.push(first.path);
        }
        theme.setCollapse(item.children.length > 1 ? false : true);
        this.childNavList = item.children;
        this.activeColumnIndex = item.k ?? 0;
      } else if (item.path) {
        router.push(item.path);
        theme.setCollapse(true);
        this.childNavList = [];
        this.activeColumnIndex = item.k ?? 0;
      }
    },
    restoreFromStorage() {
      try {
        const raw = localStorage.getItem(MENU_STORAGE_KEY);
        if (raw) {
          const tree = JSON.parse(raw) as CrmebMenuNode[];
          if (Array.isArray(tree) && tree.length) {
            this.setMenuTree(tree);
            return true;
          }
        }
      } catch {
        // ignore
      }
      return false;
    },
    clear() {
      this.menuTree = [];
      this.sidebarRouters = [];
      this.columnsNavList = [];
      this.childNavList = [];
      this.activeColumnIndex = 0;
      this.access = [];
      this.loaded = false;
      try {
        localStorage.removeItem(MENU_STORAGE_KEY);
      } catch {
        // ignore
      }
    },
    /** 拉取 EB 菜单树 */
    loadMenus() {
      return getMenusApi(buildMenuRequestParams()).then((res: any) => {
        const tree = (res?.data ?? res ?? []) as CrmebMenuNode[];
        this.setMenuTree(Array.isArray(tree) ? tree : []);
        return this.menuTree;
      });
    },
  },
});

export default useCrmebMenuStore;
